/**
 * Which colour a queue owns, and the three places it may be spent.
 *
 * <p>
 * <strong>The palette was already paid for.</strong> `style.css` defines twelve accent colours and
 * twelve matching soft washes; the product used four of them and had done since the template was
 * vendored. §1 of `docs/Counter_UX_Redesign.md` spends the rest — not by inventing colour, but by
 * saying which module and which queue owns which hue, so the answer is the same on the morning
 * screen, on the counter and in the menu rather than being decided per screen.
 * </p>
 *
 * <p>
 * <strong>The rule that stops it becoming a paint chart: one hue per card or section, spent on at
 * most three things</strong> — its icon tile, its figure, and a 3px edge. Everything else stays
 * neutral. And *at most* is the operative word: the Today band spends it on the tile alone, because
 * six equal tiles each carrying a coloured number make the numbers incomparable. Read
 * `TodayPage`'s note before adding the other two anywhere they sit in a row.
 * </p>
 *
 * <p>
 * <strong>Two reservations, and they are the load-bearing ones.</strong> `danger` and `warning`
 * mean late and needs-attention <em>everywhere</em>, and are never identity — the one exception is
 * a card whose whole subject *is* the late one. Amber is additionally spoken for by the financial
 * year in the header, because posting to the wrong year is expensive to unpick. **The hues say
 * which queue you are looking at; they never say how worried to be.**
 * </p>
 *
 * <p>
 * Every class is written out in full because <strong>Tailwind scans source text</strong>: a
 * template literal like <code>{`bg-${hue}`}</code> generates no CSS at all and the colour silently
 * does not appear.
 * </p>
 */
// The names live in the shared core so the mobile app uses the same ones; the classes stay here.
export type { Hue } from '@core/vocabulary';
import type { Hue } from '@core/vocabulary';

export interface HueClasses {
  /** A filled tile: the accent at full strength, for a white glyph to sit on. */
  tile: string;
  /** The soft wash, for a quieter tile or a band. */
  wash: string;
  /** The hue as ink — a figure, or a glyph on its own wash. */
  ink: string;
  /** The 3px top edge on a card. */
  edge: string;
}

export const hueClasses: Record<Hue, HueClasses> = {
  primary: {
    tile: 'bg-primary',
    wash: 'bg-primary-transparent',
    ink: 'text-primary',
    edge: 'border-t-[3px] border-t-primary',
  },
  secondary: {
    tile: 'bg-secondary',
    wash: 'bg-secondary-transparent',
    ink: 'text-secondary',
    edge: 'border-t-[3px] border-t-secondary',
  },
  info: {
    tile: 'bg-info',
    wash: 'bg-info-transparent',
    ink: 'text-info',
    edge: 'border-t-[3px] border-t-info',
  },
  cyan: {
    tile: 'bg-cyan',
    wash: 'bg-cyan-transparent',
    ink: 'text-cyan',
    edge: 'border-t-[3px] border-t-cyan',
  },
  indigo: {
    tile: 'bg-indigo',
    wash: 'bg-indigo-transparent',
    ink: 'text-indigo',
    edge: 'border-t-[3px] border-t-indigo',
  },
  purple: {
    tile: 'bg-purple',
    wash: 'bg-purple-transparent',
    ink: 'text-purple',
    edge: 'border-t-[3px] border-t-purple',
  },
  pink: {
    tile: 'bg-pink',
    wash: 'bg-pink-transparent',
    ink: 'text-pink',
    edge: 'border-t-[3px] border-t-pink',
  },
  orange: {
    tile: 'bg-orange',
    wash: 'bg-orange-transparent',
    ink: 'text-orange',
    edge: 'border-t-[3px] border-t-orange',
  },
  teal: {
    tile: 'bg-teal',
    wash: 'bg-teal-transparent',
    ink: 'text-teal',
    edge: 'border-t-[3px] border-t-teal',
  },
  success: {
    tile: 'bg-success',
    wash: 'bg-success-transparent',
    ink: 'text-success',
    edge: 'border-t-[3px] border-t-success',
  },
  warning: {
    tile: 'bg-warning',
    wash: 'bg-warning-transparent',
    ink: 'text-warning',
    edge: 'border-t-[3px] border-t-warning',
  },
  danger: {
    tile: 'bg-danger',
    wash: 'bg-danger-transparent',
    ink: 'text-danger',
    edge: 'border-t-[3px] border-t-danger',
  },
};

/**
 * Which hue each screen owns, by the menu key `pages/builtKeys.ts` names it with.
 *
 * <p>
 * Taken from the mockup's sidebar, which colours every nav row: Rental teal, returns blue, deposits
 * indigo, cleaning cyan, alterations purple, the catalogue orange. Setup is deliberately left
 * <strong>out</strong> rather than given a colour of its own — it is where a shop goes twice a
 * year, and a hue as loud as the day's work says the wrong thing about both.
 * </p>
 *
 * <p>
 * <strong>A queue may take a hue this map does not give it.</strong> The Today band is per
 * <em>queue</em> and not per module: two of its cards open the same booking register on different
 * views, so they cannot both be teal or the band loses the distinction it exists for. This map is
 * the default for anything asking "what colour is this module"; a surface with a finer question
 * declares its own and says why.
 * </p>
 */
export const moduleHue: Partial<Record<string, Hue>> = {
  today: 'primary',

  /*
    The sidebar groups. A group's hue is its own rather than the first child's, because a group
    holds several: Rental covers bookings, returns, deposits and disputes, which are teal, blue,
    indigo and pink one level down. **`setup` is deliberately absent, and absent means neutral** —
    an unmapped key takes no colour, which is the answer for configuration.
  */
  rental: 'primary',
  work: 'purple',
  stock: 'orange',
  shop: 'teal',
  money: 'success',
  people: 'pink',
  insight: 'info',

  // Rental — the day's work.
  availability: 'primary',
  bookings: 'primary',
  returns: 'secondary',
  deposits: 'indigo',
  invoices: 'primary',
  approvals: 'indigo',
  disputes: 'pink',

  // Work — where a garment goes between rentals.
  cleaning: 'cyan',
  alterations: 'purple',
  fittings: 'purple',

  // Stock — the garments and what describes them, which change slowly.
  categories: 'orange',
  attributes: 'orange',
  items: 'orange',
  units: 'orange',
  receipts: 'orange',
  transfers: 'orange',
  packages: 'orange',
  ratecards: 'orange',

  // Shop — selling and buying.
  pos: 'teal',
  purchases: 'teal',
  bills: 'teal',

  // Money — what moved and what is owed.
  cash: 'success',
  expenses: 'success',
  vendorpayments: 'success',
  outstanding: 'success',

  // People — the party masters.
  customers: 'pink',
  vendors: 'pink',
  employees: 'pink',
  parties: 'pink',

  // Reports.
  reports: 'info',
  roi: 'info',
};

/**
 * The hue a menu key owns, as an ink class — and an empty string for a key that owns none.
 *
 * <p>
 * An empty string rather than a null the caller has to handle: this is concatenated into a
 * `className` at every call site, and a helper that returns `undefined` there prints the word
 * "undefined" into the class list the first time somebody forgets the `?? ''`.
 * </p>
 */
export function hueInk(key: string): string {
  const hue = moduleHue[key];

  return hue ? hueClasses[hue].ink : '';
}
